import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import type {
  DirectQuoteRequest,
  DirectQuoteLoadRow,
} from "@/hooks/useDirectQuoteRequests";
import { useBrokerQuoteRequestsPaged } from "@/hooks/useBrokerQuoteRequestsPaged";

import { supabase } from "@/integrations/supabase/client";
import { notifyClient } from "@/lib/notify";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { ArrowRight, Send, Loader2, Sparkles, Trophy, EyeOff, Eye, ExternalLink } from "lucide-react";
import { usePageMeta } from "@/hooks/usePageMeta";

function fmtMoney(n: number | null) {
  if (n == null) return "—";
  return `$${Math.round(Number(n)).toLocaleString()}`;
}
function fmtTimeLeft(deadline: string | null) {
  if (!deadline) return "Open";
  const ms = new Date(deadline).getTime() - Date.now();
  if (ms <= 0) return "Expired";
  const h = Math.floor(ms / 3600000);
  if (h < 1) return `${Math.floor(ms / 60000)}m left`;
  if (h < 48) return `${h}h left`;
  return `${Math.floor(h / 24)}d left`;
}
function fmtDate(d: string | null) {
  if (!d) return null;
  try { return new Date(d).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }); } catch { return d; }
}
function fmtPickupWindow(start: string | null, end: string | null) {
  if (!start && !end) return null;
  if (start && end) {
    const s = fmtDate(start); const e = fmtDate(end);
    return s === e ? s : `${s} – ${e}`;
  }
  return fmtDate(start || end);
}
const VOLUME_LABEL: Record<string, string> = {
  one_off: "One-off",
  recurring: "Recurring",
  flexible: "Flexible",
};

type ActiveTarget =
  | { kind: "request"; row: DirectQuoteRequest }
  | { kind: "load"; row: DirectQuoteLoadRow };

const PAGE_SIZE = 10;

/** Server-side page navigation (LIMIT/OFFSET) — 10 rows per page. */
function PaginationBar({
  page, pageCount, total, onPage,
}: { page: number; pageCount: number; total: number; onPage: (p: number) => void }) {
  if (pageCount <= 1) return null;
  const windowStart = Math.max(1, Math.min(page - 2, pageCount - 4));
  const windowEnd = Math.min(pageCount, windowStart + 4);
  const pages: number[] = [];
  for (let p = windowStart; p <= windowEnd; p++) pages.push(p);
  return (
    <div className="flex flex-wrap items-center justify-between gap-2 pt-2">
      <div className="text-xs text-muted-foreground">
        Page {page} of {pageCount} · {total} total
      </div>
      <div className="flex items-center gap-1">
        <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => onPage(page - 1)}>Prev</Button>
        {pages.map((p) => (
          <Button
            key={p}
            size="sm"
            variant={p === page ? "default" : "outline"}
            className="min-w-9 px-2"
            onClick={() => onPage(p)}
          >
            {p}
          </Button>
        ))}
        <Button size="sm" variant="outline" disabled={page >= pageCount} onClick={() => onPage(page + 1)}>Next</Button>
      </div>
    </div>
  );
}


export default function LRDirectBrokerQuotesPage() {
  usePageMeta({ title: "Quote Requests · LR Direct" });
  const { user } = useAuth();

  const [tab, setTab] = useState<"open" | "history">("open");
  const [page, setPage] = useState(1);
  useEffect(() => { setPage(1); }, [tab]);

  const {
    loads, requests, hiddenIds, mineByReq, mineByLoad,
    openTotal, historyTotal, loading, refresh,
  } = useBrokerQuoteRequestsPaged(user?.id, tab, page, PAGE_SIZE);

  // Section-visit clear: mark unread quote_request_received notifications read.
  useEffect(() => {
    if (!user?.id) return;
    supabase
      .from("notifications")
      .update({ is_read: true, read_at: new Date().toISOString() })
      .eq("recipient_user_id", user.id)
      .eq("type", "quote_request_received")
      .eq("is_read", false)
      .then(() => { /* noop */ });
  }, [user?.id]);

  const [active, setActive] = useState<ActiveTarget | null>(null);
  const [rate, setRate] = useState("");
  const [validHours, setValidHours] = useState("48");
  const [note, setNote] = useState("");
  const [altEq, setAltEq] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submittedByReq = mineByReq;
  const directSubmittedByLoad = mineByLoad;
  const openCount = openTotal;
  const historyCount = historyTotal;
  const total = tab === "open" ? openTotal : historyTotal;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));



  const submitQuote = async () => {
    if (!active) return;
    const r = Number(rate);
    if (!r || r <= 0) return toast.error("Enter a valid rate");
    setSubmitting(true);

    if (active.kind === "request") {
      const req = active.row;
      const validUntil = validHours
        ? new Date(Date.now() + Number(validHours) * 3600 * 1000).toISOString()
        : null;
      const { error } = await supabase.rpc("submit_direct_quote_response", {
        p_request_id: req.id,
        p_rate: r,
        p_valid_until: validUntil,
        p_capacity_note: note || null,
        p_alt_equipment: altEq || null,
      });
      setSubmitting(false);
      if (error) return toast.error(error.message);
      try {
        const lane = `${req.origin_city}, ${req.origin_state} → ${req.destination_city}, ${req.destination_state}`;
        const amountStr = `$${r.toLocaleString("en-US", { maximumFractionDigits: 0 })}`;
        // Resolve responder's real company name.
        let brokerCompany = "A broker";
        if (user?.id) {
          try {
            const { data: cuRow } = await supabase
              .from("company_users")
              .select("companies:company_id(name)")
              .eq("user_id", user.id)
              .limit(1)
              .maybeSingle();
            const coName = (cuRow as any)?.companies?.name as string | undefined;
            if (coName && coName.trim()) {
              brokerCompany = coName.trim();
            } else {
              const { data: prof } = await supabase
                .from("profiles")
                .select("fmcsa_dba_name, fmcsa_legal_name")
                .eq("id", user.id)
                .maybeSingle();
              const fallback = (prof as any)?.fmcsa_dba_name || (prof as any)?.fmcsa_legal_name;
              if (fallback && String(fallback).trim()) brokerCompany = String(fallback).trim();
            }
          } catch { /* keep default */ }
        }
        if ((req as any).created_by) {
          notifyClient({
            recipientUserId: (req as any).created_by,
            eventType: "quote_response_received",
            inAppType: "quote_response_received",
            title: `💬 Quote Response`,
            body: `${brokerCompany} quoted ${amountStr} on ${lane}`,
            link: `/lr-direct/my-quotes/${req.id}`,
            metadata: { request_id: req.id, rate: r },
            smsBody: `New quote: ${brokerCompany} quoted ${amountStr} on ${lane}`,
            emailPayload: { lane, amount: amountStr, broker_company: brokerCompany },
          });
        }
      } catch (e) { console.warn("quote_response_received notify failed", e); }
    } else {
      const load = active.row;
      const { data, error } = await supabase.rpc("submit_direct_bid" as any, {
        p_direct_load_id: load.id,
        p_bid_amount: r,
        p_message: note.trim() || null,
        p_ready_to_cover: false,
        p_truck_in_area: false,
        p_team_available: false,
        p_eta_hours: null,
      } as any);
      setSubmitting(false);
      const result = data as { success?: boolean; error?: string; code?: string } | null;
      if (error || !result?.success) {
        const friendly = result?.error || error?.message || "Something went wrong";
        return toast.error(friendly);
      }
    }

    toast.success("Quote submitted");
    setActive(null);
    setRate(""); setNote(""); setAltEq(""); setValidHours("48");
    refresh();
  };

  const hide = async (requestId: string) => {
    const { error } = await supabase.rpc("hide_quote_request_for_broker", { p_request_id: requestId });
    if (error) return toast.error(error.message);
    toast.success("Moved to history");
    refresh();
  };

  const unhide = async (requestId: string) => {
    const { error } = await supabase.rpc("unhide_quote_request_for_broker", { p_request_id: requestId });
    if (error) return toast.error(error.message);
    toast.success("Restored to open");
    refresh();
  };

  const renderRequestCard = (r: DirectQuoteRequest) => {
    const mine = submittedByReq.get(r.id);
    const pickup = fmtPickupWindow(r.pickup_window_start, r.pickup_window_end);
    const originZip = r.origin_postal_code ? ` ${r.origin_postal_code}` : "";
    const destZip = r.destination_postal_code ? ` ${r.destination_postal_code}` : "";
    return (
      <Card key={`req-${r.id}`} className="p-3 sm:p-4">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3 md:gap-4">
          <div className="min-w-0 flex-1 space-y-2">
            <div className="flex items-start flex-wrap gap-x-2 gap-y-1 text-base md:text-sm font-semibold md:font-medium">
              <span className="break-words">{r.origin_city}, {r.origin_state}{originZip}</span>
              <ArrowRight className="h-3 w-3 text-muted-foreground shrink-0 mt-1.5" />
              <span className="break-words">{r.destination_city}, {r.destination_state}{destZip}</span>
            </div>
            <div className="flex items-center flex-wrap gap-1.5">
              <Badge variant="outline" className="text-xs">{r.equipment_type}</Badge>
              <Badge variant="secondary" className="text-[10px] uppercase tracking-wide">RFQ</Badge>
              {r.volume_pattern && (
                <Badge variant={r.volume_pattern === "recurring" ? "secondary" : "outline"} className="text-xs">
                  {VOLUME_LABEL[r.volume_pattern] ?? r.volume_pattern}
                </Badge>
              )}
              <Badge variant="outline" className="text-xs md:hidden">{fmtTimeLeft(r.deadline)}</Badge>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-xs">
              {pickup && (<div><span className="text-muted-foreground">Pickup:</span> <span className="font-medium">{pickup}</span></div>)}
              <div><span className="text-muted-foreground">Quote deadline:</span> <span className="font-medium">{fmtDate(r.deadline)}</span></div>
              {r.weight_lbs != null && (<div><span className="text-muted-foreground">Weight:</span> <span className="font-medium">{Number(r.weight_lbs).toLocaleString()} lbs</span></div>)}
              {r.commodity && (<div className="min-w-0"><span className="text-muted-foreground">Commodity:</span> <span className="font-medium break-words">{r.commodity}</span></div>)}
              {r.target_rate && r.target_rate_visible_to_brokers && (
                <div><span className="text-muted-foreground">Shipper target:</span> <span className="font-medium text-primary">{fmtMoney(Number(r.target_rate))}</span></div>
              )}
            </div>
            {r.recurrence_note && (
              <div className="text-xs">
                <div className="text-muted-foreground mb-0.5">Recurrence</div>
                <div className="whitespace-pre-wrap break-words">{r.recurrence_note}</div>
              </div>
            )}
            {r.notes && (
              <div className="text-xs">
                <div className="text-muted-foreground mb-0.5">Shipper notes</div>
                <div className="whitespace-pre-wrap break-words bg-muted/40 rounded p-2">{r.notes}</div>
              </div>
            )}
          </div>
          <div className="flex items-center justify-end gap-2 md:gap-3 md:flex-col md:items-end border-t md:border-t-0 pt-2 md:pt-0 md:min-w-[140px]">
            <Badge variant="outline" className="hidden md:inline-flex text-xs">{fmtTimeLeft(r.deadline)}</Badge>
            {mine && mine.status === "active" ? (
              <div className="text-right">
                <div className="font-semibold text-sm">{fmtMoney(Number(mine.rate))}</div>
                <Badge variant="outline" className="text-xs">Quoted</Badge>
              </div>
            ) : (
              <Button size="sm" className="flex-1 md:flex-none md:w-full" onClick={() => setActive({ kind: "request", row: r })}>
                <Send className="h-4 w-4 mr-1" /> Quote
              </Button>
            )}
            <Button size="sm" variant="ghost" title="Hide" onClick={() => hide(r.id)}>
              <EyeOff className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </Card>
    );
  };

  const renderDirectCard = (l: DirectQuoteLoadRow) => {
    const mine = directSubmittedByLoad.get(l.id);
    const originZip = l.origin_postal_code ? ` ${l.origin_postal_code}` : "";
    const destZip = l.destination_postal_code ? ` ${l.destination_postal_code}` : "";
    const target = l.target_rate_min && l.target_rate_max
      ? `${fmtMoney(Number(l.target_rate_min))} – ${fmtMoney(Number(l.target_rate_max))}`
      : l.target_rate_min ? fmtMoney(Number(l.target_rate_min))
      : l.target_rate_max ? fmtMoney(Number(l.target_rate_max))
      : null;
    const visibility = l.visibility_mode === "private_only" ? "Private" : l.visibility_mode === "both" ? "Private + Open" : "Open Market";
    return (
      <Card key={`load-${l.id}`} className="p-3 sm:p-4 border-l-4 border-l-amber-400">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3 md:gap-4">
          <div className="min-w-0 flex-1 space-y-2">
            <div className="flex items-start flex-wrap gap-x-2 gap-y-1 text-base md:text-sm font-semibold md:font-medium">
              <span className="break-words">{l.origin_city}, {l.origin_state}{originZip}</span>
              <ArrowRight className="h-3 w-3 text-muted-foreground shrink-0 mt-1.5" />
              <span className="break-words">{l.destination_city}, {l.destination_state}{destZip}</span>
            </div>
            <div className="flex items-center flex-wrap gap-1.5">
              <Badge variant="outline" className="text-xs">{l.equipment_type}</Badge>
              <Badge className="text-[10px] uppercase tracking-wide bg-amber-500 hover:bg-amber-500/90">Quote</Badge>
              <Badge variant="outline" className="text-[10px] uppercase tracking-wide">LR Direct</Badge>
              <Badge variant="outline" className="text-[10px]">{visibility}</Badge>
              <Badge variant="outline" className="text-xs md:hidden">{fmtTimeLeft(l.bid_deadline)}</Badge>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-xs">
              {l.pickup_date && (<div><span className="text-muted-foreground">Pickup:</span> <span className="font-medium">{fmtDate(l.pickup_date)}</span></div>)}
              {l.bid_deadline && (<div><span className="text-muted-foreground">Quote deadline:</span> <span className="font-medium">{fmtDate(l.bid_deadline)}</span></div>)}
              {l.weight_lbs != null && (<div><span className="text-muted-foreground">Weight:</span> <span className="font-medium">{Number(l.weight_lbs).toLocaleString()} lbs</span></div>)}
              {l.commodity && (<div className="min-w-0"><span className="text-muted-foreground">Commodity:</span> <span className="font-medium break-words">{l.commodity}</span></div>)}
              {target && (<div><span className="text-muted-foreground">Shipper target:</span> <span className="font-medium text-primary">{target}</span></div>)}
            </div>
            {l.shipper_notes_to_brokers && (
              <div className="text-xs">
                <div className="text-muted-foreground mb-0.5">Shipper notes</div>
                <div className="whitespace-pre-wrap break-words bg-muted/40 rounded p-2">{l.shipper_notes_to_brokers}</div>
              </div>
            )}
          </div>
          <div className="flex items-center justify-end gap-2 md:gap-3 md:flex-col md:items-end border-t md:border-t-0 pt-2 md:pt-0 md:min-w-[140px]">
            <Badge variant="outline" className="hidden md:inline-flex text-xs">{fmtTimeLeft(l.bid_deadline)}</Badge>
            {mine && mine.status === "active" ? (
              <div className="text-right">
                <div className="font-semibold text-sm">{fmtMoney(Number(mine.rate))}</div>
                <Badge variant="outline" className="text-xs">Quoted</Badge>
              </div>
            ) : (
              <Button size="sm" className="flex-1 md:flex-none md:w-full" onClick={() => setActive({ kind: "load", row: l })}>
                <Send className="h-4 w-4 mr-1" /> Quote
              </Button>
            )}
            <Button asChild size="sm" variant="ghost" title="View on board">
              <Link to={`/lr-direct/load/${l.id}`}><ExternalLink className="h-4 w-4" /></Link>
            </Button>
          </div>
        </div>
      </Card>
    );
  };

  const renderDirectHistoryCard = (l: DirectQuoteLoadRow) => {
    const mine = directSubmittedByLoad.get(l.id);
    const isExpired = l.bid_deadline ? new Date(l.bid_deadline).getTime() <= Date.now() : false;
    const label = isExpired ? "Expired" : l.status;
    return (
      <Card key={`load-h-${l.id}`} className="p-3 sm:p-4 border-l-4 border-l-amber-300">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-4">
          <div className="min-w-0 flex-1">
            <div className="flex items-start flex-wrap gap-x-2 gap-y-1 text-sm font-medium">
              <span className="break-words">{l.origin_city}, {l.origin_state}</span>
              <ArrowRight className="h-3 w-3 text-muted-foreground shrink-0 mt-1" />
              <span className="break-words">{l.destination_city}, {l.destination_state}</span>
              <Badge variant="outline" className="text-xs">{l.equipment_type}</Badge>
              <Badge className="text-[10px] uppercase tracking-wide bg-amber-500 hover:bg-amber-500/90">Quote</Badge>
              <Badge variant="outline" className="text-[10px] uppercase tracking-wide">LR Direct</Badge>
            </div>
            {mine && (
              <div className="text-xs text-muted-foreground mt-1">
                Quoted {fmtMoney(Number(mine.rate))} · {new Date(mine.created_at).toLocaleDateString()}
              </div>
            )}
          </div>
          <div className="flex items-center flex-wrap gap-2 sm:gap-3 sm:justify-end">
            <Badge variant="secondary" className="text-xs capitalize">{label}</Badge>
            {mine?.status === "awarded" && <Badge><Trophy className="h-3 w-3 mr-1" /> Awarded</Badge>}
          </div>
        </div>
      </Card>
    );
  };

  return (
    <div className="max-w-6xl mx-auto px-4 py-6 space-y-5">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Sparkles className="h-6 w-6 text-primary" /> Quote Requests
        </h1>
        <p className="text-sm text-muted-foreground">
          Indicative pricing requests from shippers — private-network RFQs plus LR Direct quote posts.
        </p>
      </div>

      <Tabs value={tab} onValueChange={(v) => setTab(v as "open" | "history")}>
        <TabsList>
          <TabsTrigger value="open">Open <Badge variant="secondary" className="ml-2">{openCount}</Badge></TabsTrigger>
          <TabsTrigger value="history">History <Badge variant="secondary" className="ml-2">{historyCount}</Badge></TabsTrigger>
        </TabsList>

        <TabsContent value="open" className="mt-4 space-y-2">
          {loading ? (
            <Skeleton className="h-32 w-full" />
          ) : openCount === 0 ? (
            <Card className="p-8 text-center text-sm text-muted-foreground">No open quote requests right now.</Card>
          ) : (
            <>
              {loads.map(renderDirectCard)}
              {requests.map(renderRequestCard)}
              <PaginationBar page={page} pageCount={pageCount} total={total} onPage={setPage} />

            </>
          )}
        </TabsContent>

        <TabsContent value="history" className="mt-4 space-y-2">
          {loading ? (
            <Skeleton className="h-32 w-full" />
          ) : historyCount === 0 ? (
            <Card className="p-8 text-center text-sm text-muted-foreground">No history yet.</Card>
          ) : (
            <>
              {loads.map(renderDirectHistoryCard)}
              {requests.map((r) => {

                const mine = submittedByReq.get(r.id);
                const isHidden = hiddenIds.has(r.id);
                const isExpired = new Date(r.deadline).getTime() <= Date.now();
                const label = isHidden ? "Hidden" : isExpired ? "Expired" : r.status;
                return (
                  <Card key={`req-h-${r.id}`} className="p-3 sm:p-4">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-4">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-start flex-wrap gap-x-2 gap-y-1 text-sm font-medium">
                          <span className="break-words">{r.origin_city}, {r.origin_state}</span>
                          <ArrowRight className="h-3 w-3 text-muted-foreground shrink-0 mt-1" />
                          <span className="break-words">{r.destination_city}, {r.destination_state}</span>
                          <Badge variant="outline" className="text-xs">{r.equipment_type}</Badge>
                          <Badge variant="secondary" className="text-[10px] uppercase tracking-wide">RFQ</Badge>
                        </div>
                        {mine && (
                          <div className="text-xs text-muted-foreground mt-1">
                            Quoted {fmtMoney(Number(mine.rate))} · {new Date(mine.created_at).toLocaleDateString()}
                          </div>
                        )}
                      </div>
                      <div className="flex items-center flex-wrap gap-2 sm:gap-3 sm:justify-end">
                        <Badge variant="secondary" className="text-xs capitalize">{label}</Badge>
                        {mine?.status === "awarded" && <Badge><Trophy className="h-3 w-3 mr-1" /> Awarded</Badge>}
                        {isHidden && (
                          <Button size="sm" variant="ghost" title="Unhide" onClick={() => unhide(r.id)}>
                            <Eye className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </Card>
                );
              })}
              <PaginationBar page={page} pageCount={pageCount} total={total} onPage={setPage} />

            </>
          )}
        </TabsContent>
      </Tabs>

      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Submit indicative quote</DialogTitle>
          </DialogHeader>
          {active && (
            <div className="space-y-3">
              <div className="text-sm">
                <div className="font-medium">
                  {active.row.origin_city}, {active.row.origin_state} → {active.row.destination_city}, {active.row.destination_state}
                </div>
                <div className="text-xs text-muted-foreground">
                  {active.row.equipment_type}
                  {active.row.weight_lbs ? ` · ${Number(active.row.weight_lbs).toLocaleString()} lbs` : ""}
                  {active.kind === "load" ? " · LR Direct quote post" : " · Private-network RFQ"}
                </div>
              </div>
              <div>
                <Label>Rate ($)</Label>
                <Input type="number" value={rate} onChange={(e) => setRate(e.target.value)} autoFocus />
              </div>
              {active.kind === "request" && (
                <div>
                  <Label>Valid for (hours)</Label>
                  <Input type="number" value={validHours} onChange={(e) => setValidHours(e.target.value)} />
                </div>
              )}
              <div>
                <Label>{active.kind === "load" ? "Message to shipper" : "Capacity note"}</Label>
                <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Optional — capacity, transit, terms" />
              </div>
              {active.kind === "request" && (
                <div>
                  <Label>Alternate equipment (optional)</Label>
                  <Input value={altEq} onChange={(e) => setAltEq(e.target.value)} placeholder="If you can offer different equipment" />
                </div>
              )}
              <p className="text-[11px] text-muted-foreground">
                Indicative pricing only. Not a commitment to cover.
              </p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setActive(null)}>Cancel</Button>
            <Button onClick={submitQuote} disabled={submitting}>
              {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
              Submit quote
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
